import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, HostBinding, Inject, PLATFORM_ID, HostListener } from '@angular/core';
import { LocalStorageService, LocalizationService, ScreenDetectedService, SeoService } from '@core/services';
import { BtnTypeColor, EActiveMobileBlock, ESelectedButtons, Pages } from '@enums';
import { ActivatedRoute, Data, NavigationEnd, Params, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { AppState } from '@appStates';
import { selectAnalogs, selectFullInstraction, selectProductInfos, selectSimilarProducts } from 'src/app/store/selectors/info-drug.selectors';
import { BzProduct, Crumb, IFavoriteProduct, IProduct, IToSearchWithATH } from '@interfaces';
import {
  ActiveFiltersSuccess,
  AddProductInFavoriteAction,
  AddProductInListAction,
  ClearFoundActionSuccess,
  DeleteOneProductFromListAction,
  DeleteProductFromFavoriteAction,
  isGetOtherCityPharmaciesAction,
  LoadDrugInfosAction, OpenInfoDragPageAction,
  PharmaciesPageState,
  ProductToFavoriteAction,
  ProductToListAction,
  ResetPageCountLoadDataAction,
  SetCurrentFiltersAction,
  setSelectedCategoryAction
} from '@actions';
import { ApplicationRoutingService } from '@application-routing';
import { selectCurrentCity } from '@selectors';
import { RestUrlService, TypeUrl } from 'src/app/core/services/api.services/settings/rest-url.service';
import { DOCUMENT, isPlatformBrowser, PlatformLocation } from '@angular/common';
import { DeleteEventNativeBackButton } from '../../shared/decorators/disable.back.button.decorator';
import { environment } from '@env';
import { JsonLDServiceService } from '../../core/services/seo.services/json-ldservice.service';
import { InfoDrugApiService } from '../../core/services/api.services/info-drug-api.service';
import { ScrollPositionLocalStorageService } from '../../core/services/storage.services/scroll-position-local-storage.service';
import { LocationStorageService } from 'src/app/core/services/storage.services/location-storage-service.service';
import { InfoDrugHelperService } from 'src/app/core/services/helpers/info-drug-helper.service';
import { AsyncCallback } from '../../shared/decorators';

@Component({
  selector: 'app-info-drug',
  templateUrl: './info-drug.component.html',
  styleUrls: ['./info-drug.component.sass']
})

export class InfoDrugComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('printdivcontent') public printdivcontent: ElementRef;
  @ViewChild('instrScroll') public instrScroll: ElementRef;
  @ViewChild('analgScroll') public analgScroll: ElementRef;
  @ViewChild('topPosition') public topPosition: ElementRef;
  @ViewChild('content') public content: ElementRef<HTMLDivElement>;
  @ViewChild('composition') public composition: ElementRef;
  @ViewChild('testimony') public testimony: ElementRef;
  @ViewChild('contraindications') public contraindications: ElementRef;
  @ViewChild('usage') public usage: ElementRef;

  @HostBinding('class.scrollbar') public addScrollbar = false;

  public seoTextInfoDrag: string;
  public colors: typeof BtnTypeColor = BtnTypeColor;
  public buttons: typeof ESelectedButtons = ESelectedButtons;
  public currentBlock: EActiveMobileBlock = EActiveMobileBlock.product;
  public openSocialPopup = false;
  public openSocialPopupInstruction = false;
  public isLong = true;
  public showDropdown = false;
  public showScrollBtn = false;
  public shortInsctruction = true;
  public images: string;
  public bannerImages: string;
  public toggleBackground: number;
  public resizedMobileImage = false;
  public currentLang: string;

  private productModelForList: BzProduct;
  private productModelForFavorite: IFavoriteProduct;

  public routeParams: Observable<Params>;
  public fullInstraction: Observable<string>;

  public productInfo: Observable<IProduct> = this.store.pipe(select(selectProductInfos));
  public analogsBZ: Observable<BzProduct[]> = this.store.pipe(select(selectAnalogs));
  public similarBZ: Observable<BzProduct[]> = this.store.pipe(select(selectSimilarProducts));
  public currentCity: Observable<string> = this.store.select(selectCurrentCity);

  private componentDestroyer: Subject<void> = new Subject();

  @HostListener('scroll', ['$event']) onScroll(event: Event): void {
    const offsetTop: number = (event.target as HTMLElement).scrollTop;
    this.showScrollBtn = offsetTop > 350;
  }

  @HostListener('window:scroll', ['$event']) onMobileScroll(): void {
    const offsetTop: number = this.document.documentElement.scrollTop;
    this.showScrollBtn = offsetTop > 450;
  }

  @HostListener('window:popstate', ['$event']) onPopState(): void {
    if (this.screenDetectedService.isMobile()) {
      if (this.currentBlock !== EActiveMobileBlock.product) {
        this.currentBlock = EActiveMobileBlock.product;
      }
    }
  }

  trackById = (_: unknown, item: BzProduct) => item.id;

  constructor(
    public readonly screenDetectedService: ScreenDetectedService,
    public readonly locationService: LocationStorageService,
    // tslint:disable-next-line:ban-types
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    @Inject(DOCUMENT) private readonly document: Document,
    private activatedRoutes: ActivatedRoute,
    private readonly location: PlatformLocation,
    private readonly infoDrugApiService: InfoDrugApiService,
    private readonly spls: ScrollPositionLocalStorageService,
    private readonly url: RestUrlService,
    private readonly localize: LocalizationService,
    private readonly applicationRoutingService: ApplicationRoutingService,
    private readonly seo: SeoService,
    private readonly jld: JsonLDServiceService,
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly infoDrugHelper: InfoDrugHelperService,
    private readonly localStorageService: LocalStorageService
  ) {
    this.bannerImages = this.url.getUrl(TypeUrl.img) + '/ad/';
    this.images = this.url.getUrl(TypeUrl.img) + '/500-375/';
    this.initPage();
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(
      take(1)
    ).subscribe(params => {
      if (this.screenDetectedService.isMobile()) {
        if (params.i === 'short' || params.i === 'full') {
          this.currentBlock = EActiveMobileBlock.instrustion;
        }
      }
    });

    this.currentLang = this.localize.getLang();

    if (this.screenDetectedService.isMobile()) {
      this.store.dispatch(PharmaciesPageState({ currentState: null }));
    }

    this.router.events.pipe(
      takeUntil(this.componentDestroyer)
    ).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Скролим пейджу вверх на декстопе при повторной инициализации ;
        if (this.screenDetectedService.isDesktop()) {
          document.getElementsByClassName('scrollbar')[0].scrollTo({ top: 0 });
        }
        this.initShortInstruction();
      }
    });

    // this.getFullInstraction();

    if (this.screenDetectedService.isDesktop()) { this.addScrollbar = true; }

    if (isPlatformBrowser(this.platformId)) {
      this.localStorageService.addProductToViewed(+this.route.snapshot.params.id);
    }

    this.routeParams = this.route.params;
  }

  ngAfterViewInit(): void {
    if (this.route.snapshot.queryParams.i) {
      if (this.route.snapshot.queryParams.i === 'full') {
        this.getFullInstraction(true);
      }
      setTimeout(_ => this.scrollToElement('instrustion'), 500);
    }
  }

  public initPage(): void {
    this.activatedRoutes.data.pipe(
      filter((v: Data) => !!(v && !!v.infoDrag)),
      tap(({ infoDrag }): void => {
        this.setSeoContent(infoDrag.product);
        this.infoDrugHelper.setDataToNgRxStore(infoDrag);

      }),
      takeUntil(this.componentDestroyer)
    ).subscribe();
  }

  public addProductTolist(product: IProduct): void {
    this.productModelForList = {
        availability: false,
        count: product.pharmaciesCount,
        id: product.id,
        img: product.image,
        isOrder: false,
        manufac: product.productInfo.manufacturer,
        menu: [],
        middle_p: product.productInfo.avgPrice,
        name: product.name,
        priceBlister: product.productInfo.priceBlister,
        blisterNum: product.productInfo.blisterNum,
        packageNum: product.productInfo.packageNum,
        orderedContentNum: product.orderedContentNum,
        packageContentNum: product.packageContentNum,
        packageContentType: product.packageContentType,
        priceRange: product.productInfo.priceRange,
        inList: product.isList
    };

    this.localStorageService.addProductTolist(this.productModelForList);

    if (this.productModelForList.inList) {
      this.store.dispatch(DeleteOneProductFromListAction({ product: this.productModelForList.id }));
    } else {
      this.store.dispatch(AddProductInListAction({ product: this.productModelForList.id }));
    }

    this.store.dispatch(ProductToListAction());
  }

  public addProductToFavorite(product: IProduct): void {
    this.productModelForFavorite = {
      id: product.id,
      isFavorite: product.isFavorite
    };

    this.localStorageService.addProductToFavorite(this.productModelForFavorite.id);

    if (this.productModelForFavorite.isFavorite) {
      this.store.dispatch(DeleteProductFromFavoriteAction({ product: this.productModelForFavorite.id }));
    } else {
      this.store.dispatch(AddProductInFavoriteAction({ id: this.productModelForFavorite.id }));
    }

    this.store.dispatch(ProductToFavoriteAction());

  }

  public getFullInstraction(scroll = false): void {
        this.infoDrugApiService.getFullInstraction(this.route.snapshot.params.id)
      .subscribe((result: boolean) => {
        if (scroll && result) {
          this.shortInsctruction = false;
          this.isLong = false;
        }
      });
        this.fullInstraction = this.store.pipe(select(selectFullInstraction));
  }

  public getCurrentLinkAdress(): string {
    return window.location.href;
  }

  public resizeMobileImage(): void {
    if (this.screenDetectedService.isMobile()) {
      this.resizedMobileImage = true;
    }
  }

  public closeMobileImage(): void {
    const popUpImage: Element = document.getElementsByClassName('image-wrapper-popup')[0];
    popUpImage.classList.add('closePopUp');
  }

  public scrollToElement(id: string): void {
    switch (id) {
      case 'instrustion':
        if (this.screenDetectedService.isMobile()) {
          const headerOffset = 65;
          const elementPosition = this.content.nativeElement.querySelector('.short-ditails-title').getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        } else {
          this.content.nativeElement.querySelector('.short-ditails-title').scrollIntoView({
            behavior: 'smooth',
            block: this.screenDetectedService.isMobile() ? 'center' : 'start'
          });
        }
        break;
      case 'analogs':
        this.analogsBZ.pipe(
          take(1)
        ).subscribe((products: BzProduct[]) => {
          if (products.length) {
            this.content.nativeElement.querySelector('.analogs').scrollIntoView({ behavior: 'smooth' });
          }
        });
        break;
    }
  }

  public onButtonGroupClick($event: any): void {
    const clickedElement = $event.target || $event.srcElement;
    if (clickedElement.className === 'toggle-button') {
      const isCertainButtonAlreadyActive: HTMLElement = clickedElement.parentElement.querySelector('.active');
      if (isCertainButtonAlreadyActive) {
        isCertainButtonAlreadyActive.classList.remove('active');
      }
      clickedElement.className += ' active';
    }
    this.toggleBackground = Number(clickedElement.value);
  }

  public printPage(): void {
    window.print();
  }

  public crumbsFilter = (crumb) => !crumb.productId && crumb?.info !== 'preparat';

  public toggleInstrution(): void {
    if (this.isLong === true) {
      this.isLong = false;
      this.shortInsctruction = false;
      this.deleteActiveClass();
      this.getFullInstraction();
    } else {
      this.isLong = true;
      this.shortInsctruction = true;
      this.toggleBackground = null;
    }
  }

  public initShortInstruction(): void {
    this.isLong = true;
    this.shortInsctruction = true;
    this.toggleBackground = null;
  }

  public deleteActiveClass(): void {
    const element: HTMLElement = document.querySelector('.active');
    if (element) {
      element.classList.remove('active');
    }
  }

  public openSocialBtn(event: Event): void {
    this.openSocialPopup = !this.openSocialPopup;
    if (this.openSocialPopup === true) {
      event.stopPropagation();
    }
  }

  public openSocialBtnInsruction(): void {
    this.openSocialPopupInstruction = !this.openSocialPopupInstruction;
  }

  public toPharmacies(other: boolean = false): void {
    this.store.dispatch(isGetOtherCityPharmaciesAction({ other }));
    this.route.params.pipe(take(1)).subscribe((params: Params) => {
      this.router.navigate(this.localize.getReturnParamsLang(['pharmacies', params.url, this.locationService.townEn]));
    });
  }

  public getRouterLinkToPharmcies(): string {
    return this.localize.getRouterLinkApp('pharmacies');
  }

  public backToShortInstruction(): void {
    if (this.isLong === false) {
      this.shortInsctruction = true;
      this.isLong = true;
    }
  }

  public moveBlockToView(id: number): void {
    switch (id) {
      case 0:
        const scrollToComposition: HTMLElement = this.composition.nativeElement;
        scrollToComposition.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'center'
        });
        break;
      case 1:
        const scrollToTestimony: HTMLElement = this.testimony.nativeElement;
        scrollToTestimony.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'center'
        });
        break;
      case 2:
        const scrollToContraindications: HTMLElement = this.contraindications.nativeElement;
        scrollToContraindications.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'center'
        });
        break;
      case 3:
        const scrollToUsage: HTMLElement = this.usage.nativeElement;
        scrollToUsage.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'center'
        });
        break;
      default:
        break;
    }
  }

  public linkATHToSearch(item: IToSearchWithATH): void {
    this.spls.scrollPosition = '0';
    this.store.dispatch(ClearFoundActionSuccess());
    this.router.navigate(this.localize.getReturnParamsLang(['search', item.value]), {
      queryParams: {
        sectionId: 3,
        info: item.value,
        pattern: item.value,
        pStart: 1,
        pEnd: 1
      }
    });
  }

  public toCatalog(): void {
    this.store.dispatch(setSelectedCategoryAction({ category: null }));
    this.router.navigate(this.localize.getReturnParamsLang(['catalog']));
  }

  public toggleDropDown(): void {
    this.showDropdown = true ? this.showDropdown === false : this.showDropdown = false;
  }

  @DeleteEventNativeBackButton()
  public switchTabs(element: EActiveMobileBlock): void {
    this.currentBlock = element;
    if (element === 1) {
      if (this.screenDetectedService.isMobile()) {
        this.changeCellPaddingInTables();
      }
    }
  }

  public toSearchAnalogs(product: IProduct): void {
    this.store.dispatch(ResetPageCountLoadDataAction());
    this.store.dispatch(ClearFoundActionSuccess());
    this.router.navigate(this.localize.getReturnParamsLang(['search', 'analogs']), {
      queryParams: {
        sectionId: product.id,
        info: product.productInfo.ath,
        pattern: product.productInfo.substance,
        pStart: 1,
        pEnd: 1
      }
    });
  }

  toOtherPharmacies(): void {
    this.routeParams.pipe(
      filter((value) => !!value),
      take(1)
    ).subscribe((params) => {
      this.router.navigate(this.localize.getReturnParamsLang(['pharmacies', params.url, this.locationService.townEn]));
    });
  }

  public getHomeLink(): string {
    return this.localize.getRouterLinkApp('/');
  }

  public toLinkHrefCustomCatalog(crumb: Crumb): string {
    if (crumb.info === 'trade') {
      return this.localize.getRouterLinkApp('list') + '/' + crumb.url;
    }
    if (crumb.info === '3') {
      return this.localize.getRouterLinkApp('category') + '/' + crumb.url;
    }
    return this.localize.getRouterLinkApp('catalog') + '/' + crumb.url;
  }

  @AsyncCallback()
  public pushBtnReturn(): void {
    if (this.currentBlock) {
      this.currentBlock = EActiveMobileBlock.product;
      this.route.queryParams.pipe(take(1)).subscribe((params: Params) => {
        if (params) {
          this.router.navigate([window.location.pathname]);
        }
      });
    } else {
      this.applicationRoutingService.returnPrevPageMobile();
    }
  }

  public scrollToTop(): void {
    if (this.screenDetectedService.isDesktop()) {
      const topElement: HTMLElement = this.topPosition.nativeElement;
      topElement.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  public setDefaultModileState(): void {
    this.toggleBackground = null;
    if (this.screenDetectedService.isDesktop()) { document.getElementsByClassName('active')[0]?.classList.remove('active'); }
    if (this.screenDetectedService.isMobile()) {
      if (this.currentBlock === EActiveMobileBlock.analogs) {
        this.currentBlock = EActiveMobileBlock.product;
      }
    }
  }

  public clickToAnalog(item: BzProduct): void {
    this.setDefaultModileState();
  }

  public redirectToProductInfo(item: BzProduct): void {
    const { id } = item;
    this.store.dispatch(OpenInfoDragPageAction({ idDrag: id.toString() }));
    this.setDefaultModileState();
  }

  public clearSearchState(): void {
    this.store.dispatch(SetCurrentFiltersAction({ current: [] }));
    this.store.dispatch(ActiveFiltersSuccess({ count: 0 }));
    this.store.dispatch(ClearFoundActionSuccess());
    this.store.dispatch(ResetPageCountLoadDataAction());
  }

  @AsyncCallback()
  toHomePage(): void {
    const link: string = this.localize.getLang() === 'ru' ? '/' : '/ua';
    this.router.navigate([link]).then();
    this.clearSearchState();
  }

  public toAnchorFromLongInstruction(anchor: string): void {
    const target = this.document.getElementById(anchor);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: this.screenDetectedService.isMobile() ? 'center' : 'start' });
    }
  }

  public setSeoContent(drag: IProduct): void {
    const crumbs: Crumb[] = drag?.crumbs || [];
    const img: string = drag.image || '';

    if (drag) {
      const webpImageUrl: string = 'https:' + environment.staticIMG + '500-375/' + img + '.webp';
      this.seo.setStaticTitleWithOutTranslate(drag.seo_title);
      this.seo.setStaticDescriptionWithOutTranslate(drag.seo_description);

      this.seo.getTegRobots(Pages.info);
      this.seo.updateLinkHreflang();

      this.seo.addFacebookCard({
        title: drag.seo_title ?? '',
        description: drag.seo_description ?? '',
        image: webpImageUrl,
      });

      const imageAlt: string = this.localize.getLang() === 'ua' ? `Ціни на ${drag.name}` : `Цены на ${drag.name}`;
      this.seo.addTwitterCard({
        title: drag.seo_title ?? '',
        description: drag.seo_description ?? '',
        image: webpImageUrl,
        alt: imageAlt,
      });

      this.jld.add(
        JsonLDServiceService.Breadcrambs(
          JsonLDServiceService.mutationCrambs(
            this.jld.getSeoBreadCrumbsWithUrlField(crumbs, true)
          )
        ), 'structured-data-crumbs');

      this.jld.add(
        JsonLDServiceService.Product(drag, this.location.href ?? ''),
        'structured-data-product'
      );
      this.seoTextInfoDrag = drag.seo_text;
    }
  }

  private changeCellPaddingInTables(): void {
  }

  ngOnDestroy(): void {
    this.seo.resetAll();
    this.store.dispatch(LoadDrugInfosAction({ product: null }));
    this.componentDestroyer.next(null);
    this.componentDestroyer.complete();
    this.jld.remove();
  }

}
